import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    console.log('[QuickAdd] Morphing modalContent with productGrid. InnerHTML before morph:', modalContent.innerHTML);
    morph(modalContent, productGrid);
    console.log('[QuickAdd] Morphed modalContent. InnerHTML after morph:', modalContent.innerHTML);

    if (typeof window.wc_get_review_badge === 'function') {
      window.wc_get_review_badge();
    }

    // Re-initialize Rivyo rating widgets if functions exist
    if (typeof window.rivyoPacificRatingWidget === 'function') {
      try { window.rivyoPacificRatingWidget(); } catch(e) { console.error(e); }
    }
    if (typeof window.fetchRivyoProductReviewCollection === 'function') {
      try { window.fetchRivyoProductReviewCollection(); } catch(e) { console.error(e); }
    }
    if (typeof window.initRivyoBadge === 'function') {
      try { window.initRivyoBadge(); } catch(e) { console.error(e); }
    }

    // Local development fallback since Rivyo API blocks non-production referrer domains
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('tryshopify') || window.location.hostname.includes('myshopify')) {
      setTimeout(() => {
        const badge = modalContent.querySelector('.rivyo-instant-badge');
        if (badge && (badge.hasAttribute('hidden') || badge.innerHTML.includes('Loading'))) {
          badge.removeAttribute('hidden');
          badge.style.display = 'block';
          badge.classList.remove('rivyo-instant-badge');
          badge.classList.add('rivyo-instant-badge-mocked');

          const productId = badge.dataset.productId || '12345';
          let hash = 0;
          for (let i = 0; i < productId.length; i++) {
            hash = productId.charCodeAt(i) + ((hash << 5) - hash);
          }
          const absHash = Math.abs(hash);

          // Generate dynamic rating: 4.50 to 5.00
          const ratingVal = (4.50 + (absHash % 6) * 0.1).toFixed(2);
          // Generate dynamic review count: 2 to 15
          const reviewCount = 2 + (absHash % 14);

          // Generate star distribution based on the rating
          let star5 = reviewCount;
          let star4 = 0;
          let star3 = 0;
          let star2 = 0;
          let star1 = 0;

          if (parseFloat(ratingVal) < 5.00) {
            const pct5 = 0.6 + (absHash % 3) * 0.1;
            star5 = Math.max(1, Math.round(reviewCount * pct5));
            const remaining = reviewCount - star5;
            if (remaining > 0) {
              const pct4 = 0.7;
              star4 = Math.round(remaining * pct4);
              const remaining2 = remaining - star4;
              if (remaining2 > 0) {
                star3 = remaining2;
              }
            }
          }

          const pct5Val = Math.round((star5 / reviewCount) * 100);
          const pct4Val = Math.round((star4 / reviewCount) * 100);
          const pct3Val = Math.round((star3 / reviewCount) * 100);
          const pct2Val = Math.round((star2 / reviewCount) * 100);
          const pct1Val = Math.round((star1 / reviewCount) * 100);

          badge.innerHTML = `
            <style>
              .rivyo-mock-badge-wrapper:hover .rivyo-mock-tooltip {
                display: block !important;
              }
            </style>
            <div class="rivyo-mock-badge-wrapper" style="position: relative; display: inline-flex; align-items: center; gap: 6px; font-size: 15px; color: #161616; font-family: inherit; cursor: pointer;">
              <span style="color: #FFD152; display: inline-flex; align-items: center;">
                <svg fill="#FFD152" width="20" height="20" viewBox="0 0 24 24" style="margin-right: 1px;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                <svg fill="#FFD152" width="20" height="20" viewBox="0 0 24 24" style="margin-right: 1px;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                <svg fill="#FFD152" width="20" height="20" viewBox="0 0 24 24" style="margin-right: 1px;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                <svg fill="#FFD152" width="20" height="20" viewBox="0 0 24 24" style="margin-right: 1px;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                <svg fill="#FFD152" width="20" height="20" viewBox="0 0 24 24" style="margin-right: 1px;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
              </span>
              <span style="font-weight: 600; margin-left: 2px;">${ratingVal}</span>
              <span style="color: #767676; font-size: 14px;">(${reviewCount} Reviews)</span>
              
              <!-- Tooltip Popup -->
              <div class="rivyo-mock-tooltip" style="display: none; position: absolute; top: calc(100% + 8px); left: 0; background: #ffffff; border: 1px solid #e0e0e0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 4px; padding: 15px; width: 260px; z-index: 10000; text-transform: none;">
                <div class="rivyo-mock-tooltip-arrow" style="position: absolute; top: -6px; left: 30px; width: 10px; height: 10px; background: #ffffff; border-left: 1px solid #e0e0e0; border-top: 1px solid #e0e0e0; transform: rotate(45deg); z-index: 10001;"></div>
                <div class="rivyo-mock-tooltip-title" style="font-size: 14px; font-weight: 600; color: #333333; margin-bottom: 12px; text-align: center;">${ratingVal} out of 5 stars</div>
                <div class="rivyo-mock-tooltip-rows" style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
                  <!-- Row 5 -->
                  <div class="rivyo-mock-tooltip-row" style="display: flex; flex-direction: row; align-items: center; font-size: 13px; color: #555555; width: 100%;">
                    <span class="rivyo-mock-star-num" style="width: 12px; font-weight: 500; display: inline-block;">5</span>
                    <svg fill="#FFD152" width="12" height="12" viewBox="0 0 24 24" style="margin: 0 8px 0 4px; display: inline-block;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    <div class="rivyo-mock-progress-bar-container" style="flex-grow: 1; min-width: 100px; height: 8px; background: #eeeeee; border-radius: 4px; overflow: hidden; margin-right: 10px; display: block; position: relative;">
                      <div class="rivyo-mock-progress-bar-filled" style="height: 100%; background-color: #9bc5b9; border-radius: 4px; display: block; width: ${pct5Val}%; position: absolute; left: 0; top: 0;"></div>
                    </div>
                    <span class="rivyo-mock-pct-num ${pct5Val === 0 ? 'empty' : ''}" style="width: 35px; text-align: right; font-weight: 500; color: ${pct5Val === 0 ? '#999999' : '#555555'}; display: inline-block;">${pct5Val}%</span>
                  </div>
                  <!-- Row 4 -->
                  <div class="rivyo-mock-tooltip-row" style="display: flex; flex-direction: row; align-items: center; font-size: 13px; color: #555555; width: 100%;">
                    <span class="rivyo-mock-star-num" style="width: 12px; font-weight: 500; display: inline-block;">4</span>
                    <svg fill="#FFD152" width="12" height="12" viewBox="0 0 24 24" style="margin: 0 8px 0 4px; display: inline-block;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    <div class="rivyo-mock-progress-bar-container" style="flex-grow: 1; min-width: 100px; height: 8px; background: #eeeeee; border-radius: 4px; overflow: hidden; margin-right: 10px; display: block; position: relative;">
                      <div class="rivyo-mock-progress-bar-filled" style="height: 100%; background-color: #9bc5b9; border-radius: 4px; display: block; width: ${pct4Val}%; position: absolute; left: 0; top: 0;"></div>
                    </div>
                    <span class="rivyo-mock-pct-num ${pct4Val === 0 ? 'empty' : ''}" style="width: 35px; text-align: right; font-weight: 500; color: ${pct4Val === 0 ? '#999999' : '#555555'}; display: inline-block;">${pct4Val}%</span>
                  </div>
                  <!-- Row 3 -->
                  <div class="rivyo-mock-tooltip-row" style="display: flex; flex-direction: row; align-items: center; font-size: 13px; color: #555555; width: 100%;">
                    <span class="rivyo-mock-star-num" style="width: 12px; font-weight: 500; display: inline-block;">3</span>
                    <svg fill="#FFD152" width="12" height="12" viewBox="0 0 24 24" style="margin: 0 8px 0 4px; display: inline-block;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    <div class="rivyo-mock-progress-bar-container" style="flex-grow: 1; min-width: 100px; height: 8px; background: #eeeeee; border-radius: 4px; overflow: hidden; margin-right: 10px; display: block; position: relative;">
                      <div class="rivyo-mock-progress-bar-filled" style="height: 100%; background-color: #9bc5b9; border-radius: 4px; display: block; width: ${pct3Val}%; position: absolute; left: 0; top: 0;"></div>
                    </div>
                    <span class="rivyo-mock-pct-num ${pct3Val === 0 ? 'empty' : ''}" style="width: 35px; text-align: right; font-weight: 500; color: ${pct3Val === 0 ? '#999999' : '#555555'}; display: inline-block;">${pct3Val}%</span>
                  </div>
                  <!-- Row 2 -->
                  <div class="rivyo-mock-tooltip-row" style="display: flex; flex-direction: row; align-items: center; font-size: 13px; color: #555555; width: 100%;">
                    <span class="rivyo-mock-star-num" style="width: 12px; font-weight: 500; display: inline-block;">2</span>
                    <svg fill="#FFD152" width="12" height="12" viewBox="0 0 24 24" style="margin: 0 8px 0 4px; display: inline-block;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    <div class="rivyo-mock-progress-bar-container" style="flex-grow: 1; min-width: 100px; height: 8px; background: #eeeeee; border-radius: 4px; overflow: hidden; margin-right: 10px; display: block; position: relative;">
                      <div class="rivyo-mock-progress-bar-filled" style="height: 100%; background-color: #9bc5b9; border-radius: 4px; display: block; width: ${pct2Val}%; position: absolute; left: 0; top: 0;"></div>
                    </div>
                    <span class="rivyo-mock-pct-num ${pct2Val === 0 ? 'empty' : ''}" style="width: 35px; text-align: right; font-weight: 500; color: ${pct2Val === 0 ? '#999999' : '#555555'}; display: inline-block;">${pct2Val}%</span>
                  </div>
                  <!-- Row 1 -->
                  <div class="rivyo-mock-tooltip-row" style="display: flex; flex-direction: row; align-items: center; font-size: 13px; color: #555555; width: 100%;">
                    <span class="rivyo-mock-star-num" style="width: 12px; font-weight: 500; display: inline-block;">1</span>
                    <svg fill="#FFD152" width="12" height="12" viewBox="0 0 24 24" style="margin: 0 8px 0 4px; display: inline-block;"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    <div class="rivyo-mock-progress-bar-container" style="flex-grow: 1; min-width: 100px; height: 8px; background: #eeeeee; border-radius: 4px; overflow: hidden; margin-right: 10px; display: block; position: relative;">
                      <div class="rivyo-mock-progress-bar-filled" style="height: 100%; background-color: #9bc5b9; border-radius: 4px; display: block; width: ${pct1Val}%; position: absolute; left: 0; top: 0;"></div>
                    </div>
                    <span class="rivyo-mock-pct-num ${pct1Val === 0 ? 'empty' : ''}" style="width: 35px; text-align: right; font-weight: 500; color: ${pct1Val === 0 ? '#999999' : '#555555'}; display: inline-block;">${pct1Val}%</span>
                  </div>
                </div>
              </div>
            </div>
          `;
        }
      }, 800);
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
